/**
 * `playSequential` の連続再生中に、再生中の要素の文面と進捗を保持するフック — Issue #643。
 *
 * `attach(item)` で各要素に `onStart` / `onProgress` / `onEnd` を取り付けると、
 * `current` が「いま鳴っている要素」に追従する。
 */
import { useCallback, useState } from 'react';

import { clampProgress } from '../utils/karaoke';
import type { SequentialAudioItem } from '../utils/sequentialAudio';

export interface KaraokeState {
  label: string;
  text: string;
  progress: number;
}

export interface UseKaraokePlaybackResult {
  /** 再生中の要素 (何も鳴っていないときは null) */
  current: KaraokeState | null;
  /** 連続再生の 1 要素にカラオケ表示用のコールバックを取り付ける */
  attach: (item: SequentialAudioItem) => SequentialAudioItem;
  /** 表示を消す */
  clear: () => void;
}

export function useKaraokePlayback(): UseKaraokePlaybackResult {
  const [current, setCurrent] = useState<KaraokeState | null>(null);

  const clear = useCallback(() => setCurrent(null), []);

  const attach = useCallback(
    (item: SequentialAudioItem): SequentialAudioItem => ({
      ...item,
      onStart: () => {
        item.onStart?.();
        setCurrent({ label: item.label ?? '', text: item.text ?? '', progress: 0 });
      },
      onProgress: (ratio) => {
        item.onProgress?.(ratio);
        setCurrent((prev) => (prev ? { ...prev, progress: clampProgress(ratio) } : prev));
      },
      onEnd: () => {
        item.onEnd?.();
        setCurrent(null);
      },
    }),
    [],
  );

  return { current, attach, clear };
}
